import { useTranslation } from 'react-i18next'

import {
  Badge,
  Box,
  HStack,
  TertiaryButton,
  chakra,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { ButtonTooltip } from '~/components/common/ButtonTooltip'
import {
  COMPACT_DATE_FIELD_TIGHT_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { iconSize } from '~/constants/iconSize'
import { useWorkflowLockReasons } from '~/hooks/workflowCase/useWorkflowLockReasons'
import {
  civilDateToInputValue,
  dateToCivilDate,
  parseDateInputValue,
} from '~/models/civilDate'
import {
  addDays,
  formatDateValue,
  parseIsoDate,
  startOfTodayJst,
} from '~/models/dateTime'

/** 受渡日 CompactCivilDateField の id (= label htmlFor / test の到達点)。 */
const INTRADAY_DELIVERY_DATE_ID = 'intraday-page-header-delivery-date'

type Props = {
  /** 表示中の受渡日 (= 一覧 / チャート / 札作成が対象にする日)。 */
  targetDate: Date
  onTargetDateChange: (next: Date) => void
  /**
   * 過去日を選べるか。 入札履歴・約定履歴タブ表示中だけ true にする (AC-BIN-102: 入札履歴・
   * 約定履歴は過去の確定結果を参照する画面のため過去日も選べる)。 false (= 入札追加) は
   * 当日から未来の任意日に限る (AC-BIN-000a)。
   */
  allowPastDates?: boolean
  /**
   * ワークフローケース由来で受渡日が固定されているか (= design
   * `#intraday-case-locked-hint`)。 true のとき日付入力・前後ナビ・プリセットを無効化する。
   * caseId 付きで開いた直後、 wfCase 取得完了前から true になる (fail-closed)。
   */
  isDateLocked?: boolean
  /**
   * ケース対象日が確定済みか (= `lockedTargetDate !== undefined`)。 true の間
   * だけ Badge「ケース対象日に固定」を表示する (Design Note §States: 取得済の
   * みが対象)。省略時は false。
   */
  isTargetDateConfirmed?: boolean
}

/**
 * AC-BIN-102 / 131: 全体フィルターの受渡日。 前後ナビ・当日 / 翌日も同じ日付を更新する。
 * 開閉式の条件のカードの 1 項目として置く (見えるラベルはカードの格子が出す)。
 */
export const IntradayDeliveryDateControl = ({
  targetDate,
  onTargetDateChange,
  allowPastDates = false,
  isDateLocked = false,
  isTargetDateConfirmed = false,
}: Props): React.ReactElement => {
  const { t } = useTranslation()
  const { dateLockedReason } = useWorkflowLockReasons()

  // ケース対象日固定中 (isDateLocked) の受渡日コントロールにだけ理由 Tooltip を
  // 付ける (`SpotPageHeader` と同じ契約: 理由文言は呼び出し元へ prop を増やさず
  // `useWorkflowLockReasons` から引く)。非固定時は ButtonTooltip で包まず
  // DOM を変えない。
  const maybeLockTooltip = (child: React.ReactElement): React.ReactElement =>
    !isDateLocked ? (
      child
    ) : (
      <ButtonTooltip content={dateLockedReason}>{child}</ButtonTooltip>
    )

  /**
   * 受渡日セレクタの下限 (= 過去日選択不可)。
   *
   * `bidding/intraday-bidding.md` AC-BIN-000a は「入札追加タブでの選択可能範囲は
   * 当日から未来の任意日、 過去日は選択不可」と明記する一方、 同 AC は明示的に
   * 入札追加タブに限定する（`入札履歴・約定履歴は過去日も選択可能（過去の確定結果を
   * 参照する画面のため）` AC-BIN-102）。 下限は入札履歴サブタブ表示中
   * （`allowPastDates` = true）は適用しない。 上限は設けない方針のまま変更しない。
   */
  const minDeliveryDate = allowPastDates
    ? undefined
    : dateToCivilDate(startOfTodayJst())

  /** 受渡日を ±1 日ずらす (= 前後ナビ)。 下限は [[minDeliveryDate]] に従う (上限は設けない)。 */
  const handleStepDay = (step: number): void => {
    const next = addDays(targetDate, step)
    if (
      minDeliveryDate !== undefined &&
      next.getTime() < startOfTodayJst().getTime()
    ) {
      return
    }
    onTargetDateChange(next)
  }

  /**
   * 前日ボタンが下限到達で無効か (= 表示中の受渡日が既に今日で、 これ以上前日へは
   * 戻れない)。 `isDateLocked` とは独立の理由 (ケース固定 vs 過去日下限) なので
   * 別 state として持つ (前日ボタンの `ButtonTooltip` 分岐で優先順位を分ける)。
   */
  const isPrevDayDisabled =
    minDeliveryDate !== undefined &&
    targetDate.getTime() <= startOfTodayJst().getTime()

  return (
    // 幅が狭い時はカードのマスの中で折り返す。
    <HStack alignItems="center" flexWrap="wrap" flexShrink={0} gap={2}>
      {/* accessible name は `chakra.label htmlFor` で持つ (CompactCivilDateField は
          aria-label prop を持たず、 内部の visually-hidden native input を id + label 経由で
          名前解決する。 `SpotPageHeader` と同じ pattern)。
          見えるラベルはカードの格子 (FilterGrid) が出すので、 ここは見た目だけ隠す
          (FilterGrid のラベルは `<label htmlFor>` ではないため。 SupplyGap の
          VisuallyHiddenLabel と同じ書き方)。 */}
      <chakra.label
        css={{
          position: 'absolute',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
        }}
        htmlFor={INTRADAY_DELIVERY_DATE_ID}
      >
        {t('page:biddingIntraday.market.deliveryDate')}
      </chakra.label>
      {/* `<` 日付 `>` は折り返さない 1 組 (`>` は必ず日付の右。 2026-10-26 デザイナー)。 当日 / 翌日の
          1 組は、 マスに入らなければ次の行へ折り返す。 */}
      <HStack flexShrink={0} gap={2}>
        {/* 前後ナビ (= ±1 日移動、 issue #5172)。 「◀ ピッカー ▶」 は spot
          (`SpotPageHeader` / design `spot.html` `.spot-day-step`) と同じ作法。
          [design 差異] design `intraday.html` にはまだ前後ナビが無く、 spot 側の
          作法を先行適用している (デザイナー確認は別途)。
          [rule 逸脱] icon-only は `IconButton` とする規約だが、 隣の 当日/翌日 preset と同じ見た目 (= design の
          `btn-tertiary btn-square`) に揃える必要があるため `TertiaryButton` を使う
          (spot の前後ナビ・`components/balance/FilterCard/DateNav` と同じ判断)。
          icon 寸法は 8080 の `.spot-day-step` (svg 20×20) と共通 [[DateStepper]] に
          合わせて 20px を明示する。 icon-only の icon 寸法は DS 既定
          (24px) に任せる規約だが、 40px 角に固定した枠では 24px だと余白が 8px しか
          残らず 8080 と絵が変わるため、 枠を design 値で固定したのと同じ理由で例外にする。 */}
        {/* `<` `>` は icon だけのボタンなので、 ふだんもボタン名 (前日へ / 翌日へ) を
          Tooltip で出す (UI 辞書 `components/datepicker.md`「aria-label と Tooltip の
          両方を付ける」。 共通 [[DateStepper]] / `SpotPageHeader` と同じ)。
          押せない時は理由に差し替える。 isDateLocked (ケース固定) と
          isPrevDayDisabled (過去日下限到達、 AC-BIN-000a) は独立の無効理由で、
          ケース固定の理由を優先する。 content はどの状態でも非空なので
          `ButtonTooltip` の isHidden 切り替え (= ボタンの remount) は起きない。 */}
        <ButtonTooltip
          content={
            isDateLocked
              ? dateLockedReason
              : isPrevDayDisabled
                ? t('common:filter.prevDayDisabled')
                : t('page:biddingIntraday.deliveryDate.prevDay')
          }
        >
          <TertiaryButton
            // 高さは隣の日付部品 ([[CompactCivilDateField]] = 40px) と揃える。
            // 8080 `.btn.btn-square { aspect-ratio: 1/1 }` に合わせ幅も 40px の正方形にする
            // (md 既定は 58px で横長になる)。 共通 [[DateStepper]] と同じ作法。
            width="40px"
            minWidth="40px"
            aria-label={t('page:biddingIntraday.deliveryDate.prevDay')}
            disabled={isDateLocked || isPrevDayDisabled}
            onClick={() => handleStepDay(-1)}
            paddingX={0}
            size="md"
          >
            <FaChevronLeftRegular
              boxSize={iconSize.labeledButton}
              aria-hidden="true"
            />
          </TertiaryButton>
        </ButtonTooltip>
        {/* 幅は値が収まるところまで詰めた幅 (`COMPACT_DATE_FIELD_TIGHT_WIDTH`)。 前後の送りと 1 組で
          カードのマスに入れるため。 */}
        {maybeLockTooltip(
          <Box flexShrink={0} width={COMPACT_DATE_FIELD_TIGHT_WIDTH}>
            <CompactCivilDateField
              disabled={isDateLocked}
              id={INTRADAY_DELIVERY_DATE_ID}
              min={minDeliveryDate}
              onChange={(d) => {
                // 受渡日は「常に値がある」画面軸 (SpotPageHeader と同じ理由で
                // hasClear は付けず、 空欄化は無視して直前値を保持する。
                // PR #5323 レビュー、2026-08-13)。
                if (d === undefined) return
                // date-only 文字列は local time として解釈する (`new Date('2026-05-26')` は
                // UTC midnight 扱いで JST 以外の環境で前日にずれる。 SpotPageHeader と同規則。
                // `parseIsoDate` は同じく `new Date(y, m-1, d)` で local time 0:00 を作る)。
                const next = parseIsoDate(civilDateToInputValue(d))
                if (next !== undefined) onTargetDateChange(next)
              }}
              value={parseDateInputValue(formatDateValue(targetDate))}
            />
          </Box>,
        )}
        <ButtonTooltip
          content={
            isDateLocked
              ? dateLockedReason
              : t('page:biddingIntraday.deliveryDate.nextDay')
          }
        >
          <TertiaryButton
            width="40px"
            minWidth="40px"
            aria-label={t('page:biddingIntraday.deliveryDate.nextDay')}
            disabled={isDateLocked}
            onClick={() => handleStepDay(1)}
            paddingX={0}
            size="md"
          >
            <FaChevronRightRegular
              boxSize={iconSize.labeledButton}
              aria-hidden="true"
            />
          </TertiaryButton>
        </ButtonTooltip>
      </HStack>
      {/* 当日 / 翌日も 1 組 (片方だけ次の行に落とさない) */}
      <HStack flexShrink={0} gap={2}>
        {maybeLockTooltip(
          <TertiaryButton
            disabled={isDateLocked}
            onClick={() => onTargetDateChange(startOfTodayJst())}
            size="md"
          >
            {t('page:biddingIntraday.market.today')}
          </TertiaryButton>,
        )}
        {maybeLockTooltip(
          <TertiaryButton
            disabled={isDateLocked}
            onClick={() => onTargetDateChange(addDays(startOfTodayJst(), 1))}
            size="md"
          >
            {t('page:biddingIntraday.market.tomorrow')}
          </TertiaryButton>,
        )}
      </HStack>
      {isTargetDateConfirmed && (
        <Badge status="info">{t('common:filter.caseLockedBadge')}</Badge>
      )}
    </HStack>
  )
}
